import { FunctionComponent } from "react";

import { faBullseye, faCircleX, faPlus, faWebhook } from "@fortawesome/pro-regular-svg-icons";

import { SelectListPopover } from "@/pages/Private/pages/Contacts/components/SelectListPopover";
import { CrmProvider } from "@/pages/Private/redux/crm/crm.schema";

import { FooterActionItem } from "./FooterActionItem";
import { FooterPopoverGroup } from "./FooterPopoverGroup";
import { FooterCrmItems } from "./FooterCrmItems";

interface FooterAddToGroupProps {
	ts: (key: string) => string;
	/** True when the "Add to" popover is the open one. */
	isOpen: boolean;
	/** True while the nested "select a list" sub-popover is showing. */
	showListPopover: boolean;
	setShowListPopover: (value: boolean) => void;
	/** Toggle the "Add to" popover open/closed. */
	onToggle: () => void;
	/** Close every popover. */
	onClose: () => void;
	connectedCrmProviders: CrmProvider[];
	addToProfileTracker?: {
		addToProfileTracker?: () => void;
		addToProfileTrackerIsLoading?: boolean;
	};
	addToWebhook?: {
		addToWebhook?: () => void;
		addToWebhookIsLoading?: boolean;
	};
	addToAnotherList?: {
		onSelectList: (list: { id: number; name: string }) => void;
		onCreateNew: (name: string) => void;
		isLoading?: boolean;
		excludeListId?: number;
	};
	addToBlacklist?: {
		addToBlacklist?: () => void;
		addToBlacklistIsLoading?: boolean;
	};
	addToCrm?: {
		onSelectProvider: (provider: CrmProvider) => void;
		isLoading?: boolean;
	};
}

/**
 * The "Add to" group for the bulk-action footer: routes selected prospects to a
 * Tracker, a Webhook, another List, the Blacklist, or a connected CRM. The "Add
 * to List" item opens a nested {@link SelectListPopover}. Extracted from
 * {@link TableFooter} to keep that file under the size cap.
 */
export const FooterAddToGroup: FunctionComponent<FooterAddToGroupProps> = ({
	ts,
	isOpen,
	showListPopover,
	setShowListPopover,
	onToggle,
	onClose,
	connectedCrmProviders,
	addToProfileTracker,
	addToWebhook,
	addToAnotherList,
	addToBlacklist,
	addToCrm,
}) => {
	const isLoading = !!(
		addToProfileTracker?.addToProfileTrackerIsLoading ||
		addToWebhook?.addToWebhookIsLoading ||
		addToAnotherList?.isLoading ||
		addToBlacklist?.addToBlacklistIsLoading ||
		addToCrm?.isLoading
	);

	return (
		<>
			<FooterPopoverGroup
				dark
				align="right"
				icon={faPlus}
				isLoading={isLoading}
				isOpen={isOpen && !showListPopover}
				label={ts("addTo")}
				loadingLabel={ts("wait")}
				onToggle={onToggle}
			>
				{addToProfileTracker && (
					<FooterActionItem
						icon={faBullseye}
						isMenuItem
						label="Add to Tracker"
						onClick={() => {
							addToProfileTracker?.addToProfileTracker?.();
							onClose();
						}}
					/>
				)}
				{addToWebhook && (
					<FooterActionItem
						icon={faWebhook}
						isMenuItem
						label="Add to Webhook"
						onClick={() => {
							addToWebhook?.addToWebhook?.();
							onClose();
						}}
					/>
				)}
				{addToAnotherList && (
					<FooterActionItem
						icon={faPlus}
						isMenuItem
						label={ts("addToList")}
						onClick={() => {
							setShowListPopover(true);
						}}
					/>
				)}
				{addToBlacklist && (
					<FooterActionItem
						icon={faCircleX}
						isMenuItem
						label="Add to Blacklist"
						onClick={() => {
							addToBlacklist?.addToBlacklist?.();
							onClose();
						}}
					/>
				)}
				{addToCrm && (
					<FooterCrmItems
						connectedProviders={connectedCrmProviders}
						onClose={onClose}
						onSelectProvider={addToCrm.onSelectProvider}
					/>
				)}
			</FooterPopoverGroup>
			{showListPopover && isOpen && addToAnotherList && (
				<div className="relative">
					<div className="absolute bottom-full mb-2 right-0">
						<SelectListPopover
							excludeListId={addToAnotherList.excludeListId}
							isLoading={addToAnotherList.isLoading}
							onCreateNew={(name) => {
								addToAnotherList.onCreateNew(name);
								onClose();
								setShowListPopover(false);
							}}
							onSelectList={(list) => {
								addToAnotherList.onSelectList(list);
								onClose();
								setShowListPopover(false);
							}}
						/>
					</div>
				</div>
			)}
		</>
	);
};
